import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';

import { PrimaryButton, InlineTextButton } from '../Button/Button';
import Button from '../Button/Button';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import Form from '../Form/Form';
import { H4 } from '../Heading/Heading';

import css from './ShipStationCredentialsForm.module.css';

/**
 * ShipStationCredentialsForm component for entering and managing ShipStation API credentials.
 * Clicking Save will first validate the credentials against ShipStation before saving.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS class name
 * @param {string} [props.formId] - Form identifier
 * @param {Object} [props.initialValues] - Initial form values (apiKey, shipFromName, shipFromAddressLine1, etc.)
 * @param {Function} props.onSubmit - Form submission handler (tests then saves)
 * @param {Function} [props.onRemoveCredentials] - Remove credentials handler
 * @param {boolean} [props.hasExistingCredentials] - Whether user has saved credentials
 * @param {boolean} [props.inProgress] - Whether an operation is in progress (test or save)
 * @param {string} [props.testError] - Credential validation error (blocks saving)
 * @param {propTypes.error} [props.saveError] - Form save error
 * @param {boolean} [props.ready] - Whether form submission succeeded
 * @returns {JSX.Element}
 */
const ShipStationCredentialsForm = props => {
  const {
    className,
    formId = 'ShipStationCredentialsForm',
    initialValues,
    onSubmit,
    onRemoveCredentials,
    hasExistingCredentials = false,
    inProgress = false,
    testError = null,
    saveError,
    ready = false,
  } = props;

  const intl = useIntl();
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (ready) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [ready]);

  const handleRemoveClick = () => {
    setShowRemoveConfirmation(true);
  };

  const handleConfirmRemove = () => {
    if (onRemoveCredentials) {
      onRemoveCredentials();
    }
    setShowRemoveConfirmation(false);
  };

  const handleCancelRemove = () => {
    setShowRemoveConfirmation(false);
  };

  const apiKeyLabel = intl.formatMessage({ id: 'ShipStationCredentialsForm.apiKeyLabel' });
  const apiKeyPlaceholder = intl.formatMessage({
    id: 'ShipStationCredentialsForm.apiKeyPlaceholder',
  });
  const apiKeyRequired = validators.required(
    intl.formatMessage({ id: 'ShipStationCredentialsForm.apiKeyRequired' })
  );

  const classes = classNames(css.root, className);

  return (
    <FinalForm
      {...props}
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={fieldRenderProps => {
        const {
          handleSubmit,
          invalid,
          pristine,
        } = fieldRenderProps;

        const submitDisabled = invalid || pristine || inProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {/* Instructional Help Text - accordion */}
            <details className={css.helpAccordion}>
              <summary className={css.helpSummary}>
                <span className={css.helpSummaryContent}>
                  <svg
                    className={css.helpSummaryIcon}
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle cx="9" cy="9" r="8" fill="currentColor" opacity="0.14" />
                    <circle cx="9" cy="9" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <path
                      d="M9 8V12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <circle cx="9" cy="5.5" r="1" fill="currentColor" />
                  </svg>
                  <span>
                    <FormattedMessage id="ShipStationCredentialsForm.howToGetCredentials" />
                  </span>
                </span>
              </summary>
              <div className={css.helpSection}>
                <ol className={css.instructionsList}>
                  <li>
                    <FormattedMessage
                      id="ShipStationCredentialsForm.noAccountNote"
                      values={{
                        link: (
                          <a
                            href="https://www.shipstation.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={css.noAccountLink}
                          >
                            <FormattedMessage id="ShipStationCredentialsForm.noAccountSignUp" />
                          </a>
                        ),
                      }}
                    />
                  </li>
                  <li>
                    <FormattedMessage id="ShipStationCredentialsForm.step1" />
                  </li>
                  <li>
                    <FormattedMessage id="ShipStationCredentialsForm.step2" />
                  </li>
                  <li>
                    <FormattedMessage id="ShipStationCredentialsForm.step3" />
                  </li>
                </ol>
                <p className={css.linkContainer}>
                  <a
                    href="https://ship11.shipstation.com/settings/api"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.shipStationLink}
                  >
                    <FormattedMessage id="ShipStationCredentialsForm.goToSettings" />
                  </a>
                </p>
                <p className={css.securityNotice}>
                  <FormattedMessage id="ShipStationCredentialsForm.securityNotice" />
                </p>
              </div>
            </details>

            {/* Form Fields */}
            <div className={css.formSection}>
              <H4 as="h3">
                <FormattedMessage id="ShipStationCredentialsForm.credentialsTitle" />
              </H4>

              <FieldTextInput
                className={css.field}
                type="password"
                id={`${formId}.apiKey`}
                name="apiKey"
                autoComplete="off"
                label={apiKeyLabel}
                placeholder={apiKeyPlaceholder}
                validate={apiKeyRequired}
                disabled={hasExistingCredentials}
              />

              {hasExistingCredentials && !showRemoveConfirmation && (
                <InlineTextButton
                  className={css.removeButtonUnderField}
                  type="button"
                  onClick={handleRemoveClick}
                >
                  <FormattedMessage id="ShipStationCredentialsForm.removeButton" />
                </InlineTextButton>
              )}

              {/* Success Message */}
              {showSuccess && (
                <div className={css.success}>
                  <FormattedMessage id="ShipStationCredentialsForm.saveSuccess" />
                </div>
              )}

              {/* Validation / Save Errors */}
              {testError && !inProgress && (
                <div className={css.error}>
                  <FormattedMessage
                    id="ShipStationCredentialsForm.testError"
                    values={{ error: testError }}
                  />
                </div>
              )}
              {saveError && !inProgress && (
                <div className={css.error}>
                  <FormattedMessage id="ShipStationCredentialsForm.saveError" />
                </div>
              )}

              {/* Action Buttons */}
              <div className={css.actionButtons}>
                <PrimaryButton
                  className={css.submitButton}
                  type="submit"
                  inProgress={inProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage
                    id={
                      hasExistingCredentials
                        ? 'ShipStationCredentialsForm.updateButton'
                        : 'ShipStationCredentialsForm.saveButton'
                    }
                  />
                </PrimaryButton>
              </div>

              {/* Remove Confirmation */}
              {showRemoveConfirmation && (
                <div className={css.removeConfirmation}>
                  <p className={css.confirmationText}>
                    <FormattedMessage id="ShipStationCredentialsForm.removeConfirmation" />
                  </p>
                  <div className={css.confirmationButtons}>
                    <Button
                      className={css.confirmButton}
                      onClick={handleConfirmRemove}
                      inProgress={inProgress}
                    >
                      <FormattedMessage id="ShipStationCredentialsForm.confirmRemove" />
                    </Button>
                    <InlineTextButton className={css.cancelButton} onClick={handleCancelRemove}>
                      <FormattedMessage id="ShipStationCredentialsForm.cancelRemove" />
                    </InlineTextButton>
                  </div>
                </div>
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

ShipStationCredentialsForm.propTypes = {
  formId: PropTypes.string,
  inProgress: PropTypes.bool,
  testError: PropTypes.string,
  ready: PropTypes.bool,
  saveError: propTypes.error,
};

export default ShipStationCredentialsForm;
